import React, { useState, useEffect, useRef, FC, Dispatch, SetStateAction } from 'react'
import { useSelector } from 'react-redux'
import { getIndice } from 'selectors/algolia'
import {
  getArtworkId,
  getArtworkCategory,
  getArtworkMediums,
  getArtworkStyles,
  getArtworkSubject,
} from 'apps/saatchi/artdetail2/selectors/artwork'
import { getRelatedSeeMore } from 'apps/saatchi/artdetail2/selectors/carousel'
import { getSelectedProductSku } from 'apps/saatchi/artdetail2/selectors/product'
import { getVisiiMetadata } from 'apps/saatchi/artdetail2/selectors/general'
import { getUserID, isSessionSet, getIsArtist } from 'selectors/user/session'
import { getAlgoliaUserToken, getGeoLocation } from 'selectors/user/cookie'
import CarouselV2 from 'components/SA/Carousel/v2'
import useCarouselImageLazyLoading from 'components/SA/Carousel/v2/useCarouselImageLazyLoading'
import { getRelatedArtworks, clickArtworkCarousel, ALGOLIA, VISII } from '../helpers'
import Polaroid from '../ProductCarouselPolaroid'
import SeeMoreTile from '../SeeMoreTile'
import styles from '../ProductCarouselPolaroid.module.scss'
const MIN_ARTWORKS = 5
const LIST_ID = 'visually-similar-pdp'
const LIST_NAME = 'Visually Similar Artworks'

type VisuallySimilarCarouselProps = {
  shouldLoadComponent: boolean
  setShouldLoadComponent: Dispatch<SetStateAction<boolean>>
}

const VisuallySimilarCarousel: FC<VisuallySimilarCarouselProps> = (props) => {
  const { shouldLoadComponent, setShouldLoadComponent } = props
  /* Replacing connector */
  const algoliaSearchIndex = useSelector(getIndice)
  const userId = useSelector(getUserID)
  const artworkId = useSelector(getArtworkId)
  const category = useSelector(getArtworkCategory)
  const geoLocation = useSelector(getGeoLocation)
  const hasSession = useSelector(isSessionSet)
  const isArtist = useSelector(getIsArtist)
  const mediums = useSelector(getArtworkMediums)
  const subject = useSelector(getArtworkSubject)
  const artworkStyles = useSelector(getArtworkStyles)
  const visiiMetadata = useSelector(getVisiiMetadata)
  const algoliaUserToken = useSelector(getAlgoliaUserToken)
  const sku = useSelector(getSelectedProductSku)
  const seeMore = useSelector(getRelatedSeeMore)
  /* ============================================================== */
  const [artworks, handleArtworks] = useState<any[]>([])
  const polaroidRef = useRef<HTMLDivElement>(null)
  const [polaroidWidth, setPolaroidWidth] = useState<number>(
    polaroidRef?.current?.offsetWidth || 270
  )
  const { handleImageLazyLoading, lastImageIndexLoaded } = useCarouselImageLazyLoading({
    offset: 5,
  })
  const { destinationZone } = geoLocation
  const { link, title } = seeMore
  useEffect(() => {
    if (hasSession && shouldLoadComponent) {
      getRelatedArtworks({
        artworkId,
        callBack: handleArtworks,
        category,
        destinationZone: destinationZone,
        isArtist,
        mediums,
        metadata: visiiMetadata,
        sku,
        styles: artworkStyles,
        subject,
      })
    }
  }, [artworkId, hasSession, shouldLoadComponent, destinationZone])

  useEffect(() => {
    if (!!polaroidRef?.current?.offsetWidth) {
      setPolaroidWidth(polaroidRef?.current?.offsetWidth)
    }
  }, [polaroidRef?.current?.offsetWidth])

  const handleClick = (ev: React.SyntheticEvent<Record<string, any>>) => {
    setShouldLoadComponent(false)
    clickArtworkCarousel(ev, algoliaSearchIndex, algoliaUserToken, userId)
  }

  if (!artworks || artworks.length < MIN_ARTWORKS) {
    return null
  }

  const dataApi = artworks[0].api === VISII ? VISII : ALGOLIA
  const showMediumOnMaterial = dataApi !== ALGOLIA ? false : true

  return (
    <div data-section={LIST_ID}>
      <CarouselV2
        title={LIST_NAME}
        polaroidWidth={polaroidWidth}
        customStyle={styles.carouselPolaroidImage}
        onScrollCallback={handleImageLazyLoading}
        searchProviderAttributes={[
          { 'recommendations-container': true },
          { 'recommendations-pod-id': false },
          { 'result-id': false },
          { 'total-number-results': artworks.length },
          { 'filter-name': false },
          { 'filter-value': false },
        ]}
      >
        {artworks.map((artwork, index) => (
          <Polaroid
            key={index}
            polaroidRef={polaroidRef}
            artwork={artwork}
            onImageClick={handleClick}
            onTitleClick={handleClick}
            index={index}
            listId={LIST_ID}
            listName={LIST_NAME}
            dataApi={dataApi}
            showMediumOnMaterial={showMediumOnMaterial}
            isLazyLoading={index > lastImageIndexLoaded}
          />
        ))}
        <SeeMoreTile link={link} title={title} />
      </CarouselV2>
    </div>
  )
}

export default VisuallySimilarCarousel
